import {Button, Flex, Modal, Text} from '@mantine/core'
import {
  IconArchive,
  IconArchiveOff,
  IconCancel,
  IconCheck,
  IconDeviceFloppy,
  IconEye,
  IconEyeOff,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import {useMemo, useState} from 'react'

type Props = {
  submitVariant: 'add' | 'update' | 'accept' | 'unarchive' | 'unignore'
  submitting: boolean
  onSubmit?: () => Promise<void>

  destructiveVariant?: 'cancel' | 'delete' | 'archive' | 'reject' | 'ignore'
  onDestruct?: () => Promise<void>
  destructing?: boolean
  withDestructiveLabel?: boolean

  compact?: boolean
  disabled: boolean
}

export const EditModeButtons = ({
  submitVariant,
  onSubmit,
  submitting,

  destructiveVariant,
  onDestruct,
  destructing,
  withDestructiveLabel = false,

  compact = false,
  disabled: containerDisabled,
}: Props) => {
  const disabled = containerDisabled
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false)

  const buttonSize = compact ? 'compact-sm' : 'sm'
  const iconSize = compact ? 18 : 22

  const submitButton = useMemo(() => {
    return {
      add: {title: 'Add', color: undefined, icon: <IconDeviceFloppy stroke={1} size={iconSize} />},
      update: {title: 'Update', color: undefined, icon: <IconDeviceFloppy stroke={1} size={iconSize} />},
      accept: {title: 'Accept', color: 'green', icon: <IconCheck stroke={1} size={iconSize} />},
      unarchive: {title: 'Unarchive', color: undefined, icon: <IconArchiveOff stroke={1} size={iconSize} />},
      unignore: {title: 'Unignore', color: 'green', icon: <IconEye stroke={1} size={iconSize} />},
    }[submitVariant]
  }, [submitVariant, iconSize])

  const destructiveButton = useMemo(() => {
    if (!destructiveVariant) return null
    return {
      cancel: {title: 'Cancel', color: 'red', icon: <IconX stroke={1} size={iconSize} />},
      delete: {title: 'Delete', color: 'red', icon: <IconTrash stroke={1} size={iconSize} />},
      archive: {title: 'Archive', color: 'orange', icon: <IconArchive stroke={1} size={iconSize} />},
      reject: {title: 'Reject', color: 'red', icon: <IconCancel stroke={1} size={iconSize} />},
      ignore: {title: 'Ignore', color: 'orange', icon: <IconEyeOff stroke={1} size={iconSize} />},
    }[destructiveVariant]
  }, [destructiveVariant, iconSize])

  return (
    <div style={{display: 'flex', gap: 5}}>
      {destructiveButton && (
        <div>
          <Button
            title={destructiveButton.title}
            color={destructiveButton.color}
            variant={'light'}
            p={withDestructiveLabel ? undefined : 5}
            size={buttonSize}
            onClick={destructiveVariant === 'delete' ? () => setConfirmationDialogOpen(true) : onDestruct}
            loading={destructing}
            leftSection={withDestructiveLabel ? destructiveButton.icon : undefined}
            disabled={disabled}
          >
            {withDestructiveLabel ? destructiveButton.title : destructiveButton.icon}
          </Button>
        </div>
      )}

      <div style={{flex: 1}}>
        <Button
          type={'submit'}
          title={submitButton.title}
          leftSection={submitButton.icon}
          color={submitButton.color}
          size={buttonSize}
          onClick={onSubmit}
          loading={submitting}
          disabled={disabled}
        >
          {submitButton.title}
        </Button>
      </div>

      <Modal
        title={'Confirm deletion'}
        opened={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
      >
        <Flex direction={'column'} gap={'xs'} mt={'md'}>
          <Text>Are you sure you want to delete? This action cannot be undone.</Text>
          <Flex gap={5} justify={'end'}>
            <Button
              variant={'default'}
              onClick={() => setConfirmationDialogOpen(false)}
              fullWidth={false}
              disabled={disabled}
            >
              Cancel
            </Button>
            <Button
              variant={'filled'}
              color={'red'}
              onClick={async () => {
                await onDestruct?.()
                setConfirmationDialogOpen(false)
              }}
              loading={destructing}
              fullWidth={false}
              disabled={disabled}
            >
              Delete
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </div>
  )
}
